import { isProd, isLocalhost } from './appEnv';

// Statically-configured backend, baked in per Vercel project via VITE_API_URL.
// On prod and local dev this is the backend the app talks to. On *preview*
// deployments it doubles as the bootstrap/fallback backend (the stable sandbox
// API) that brokers the dynamic link to the matching backend preview.
const FALLBACK_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Mutable so initApiBase() can swap in a resolved backend-preview URL on
// previews. Read via getApiBaseUrl() — never capture this at module load time.
let apiBaseUrl = FALLBACK_API_URL;

export const getApiBaseUrl = (): string => apiBaseUrl;

const CACHE_KEY = 'preview_backend_url';

// Manual, sticky backend override the developer types on the login screen.
// Persisted in localStorage so it survives reloads/tabs until explicitly reset.
// Takes precedence over auto-resolution — see initApiBase().
const OVERRIDE_KEY = 'preview_backend_override';

const normalizeUrl = (url: string): string => url.trim().replace(/\/+$/, '');

/** The active manual override, or null if none is set. */
export const getApiBaseOverride = (): string | null =>
  typeof localStorage !== 'undefined' ? localStorage.getItem(OVERRIDE_KEY) : null;

/**
 * Pin every backend request to `url`, persisting across reloads. Applied
 * immediately (so the current page's next fetch hits it) and on subsequent
 * loads via initApiBase(). Pass an empty string to clear (same as reset).
 */
export const setApiBaseOverride = (url: string): void => {
  const normalized = normalizeUrl(url);
  if (!normalized) {
    clearApiBaseOverride();
    return;
  }
  localStorage.setItem(OVERRIDE_KEY, normalized);
  apiBaseUrl = normalized;
};

/**
 * Drop the manual override and fall back to the statically-configured backend.
 * Also clears the auto-resolve cache so the next reload re-resolves the
 * matching backend preview from scratch.
 */
export const clearApiBaseOverride = (): void => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(OVERRIDE_KEY);
  }
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(CACHE_KEY);
  }
  apiBaseUrl = FALLBACK_API_URL;
};

/**
 * Whether to surface the manual backend-override field. Shown on any non-prod
 * build, when running locally (local .env ships VITE_APP_ENV=prod, so isProd()
 * alone misses localhost), and on every Vercel preview deployment — including
 * previews of the prod-tracking project.
 */
export const canOverrideBackend = (): boolean =>
  !isProd() || isLocalhost() || __VERCEL_ENV__ === 'preview';

/**
 * On Vercel preview deployments, retarget the app from the shared sandbox
 * backend to the backend preview built from the *same* git branch, so a
 * preview frontend can be tested end-to-end against its sibling backend.
 *
 * Resolution is brokered by the stable sandbox backend (FALLBACK_API_URL) via
 * GET /preview/backend-url. The result is cached in sessionStorage for the tab.
 * Any failure (no backend preview for this branch, network/CORS error) leaves
 * the sandbox fallback in place. No-op outside previews — resolves immediately,
 * so prod and local dev render with zero delay.
 */
export async function initApiBase(): Promise<void> {
  // A manual override (set on the login screen) wins over everything, on any
  // env — that's the whole point of it. Apply and skip auto-resolution.
  const override = getApiBaseOverride();
  if (override) {
    apiBaseUrl = override;
    return;
  }

  // Only a GENUINE preview should retarget to a per-branch backend preview.
  // Vercel reports VERCEL_ENV=preview for Custom Environments too (e.g. the
  // `sandbox` env), so keying off __VERCEL_ENV__ alone wrongly treated the
  // sandbox as a preview and routed it cross-origin to a backend-preview URL
  // (CORS). A custom env carries its name in __VERCEL_TARGET_ENV__ (e.g.
  // 'sandbox'); a real preview is '' (older Vercel) or 'preview'. In a custom
  // env we skip the override and use the statically-configured /api → api-<env>.
  const isGenuinePreview =
    __VERCEL_ENV__ === 'preview' &&
    (__VERCEL_TARGET_ENV__ === '' || __VERCEL_TARGET_ENV__ === 'preview');
  if (!isGenuinePreview || !__VERCEL_GIT_COMMIT_REF__) return;

  const ref = __VERCEL_GIT_COMMIT_REF__;

  // The stable dev alias (the `develop` branch) is already pinned to the api-dev
  // backend via BACKEND_ORIGIN, so its same-origin /api hits the right backend.
  // Resolving a per-branch "preview backend" for develop is redundant (it would
  // point back at the same deployment) and only surfaces a 503 when the broker
  // token is unset. The lookup matters solely for ephemeral feature-branch
  // previews — skip it for develop.
  if (ref === 'develop') return;

  const cached = sessionStorage.getItem(CACHE_KEY);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed?.ref === ref && parsed?.apiBaseUrl) {
        apiBaseUrl = parsed.apiBaseUrl;
        return;
      }
    } catch {
      /* malformed cache — fall through and re-resolve */
    }
  }

  try {
    const resp = await fetch(
      `${FALLBACK_API_URL}/preview/backend-url?ref=${encodeURIComponent(ref)}`
    );
    if (!resp.ok) return; // 404 = no backend preview for this branch; keep sandbox
    const data = await resp.json();
    if (data?.apiBaseUrl) {
      apiBaseUrl = data.apiBaseUrl;
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ref, apiBaseUrl }));
    }
  } catch {
    /* network/CORS failure — keep the sandbox fallback */
  }
}
